/**
 * Sysadmin Studio Catalog → Agents (scaffolding).
 *
 * Lists system_agents (incl. the three Studio Helpers) + Sheet-based
 * create/edit. Polished UI is v1.2.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Bot, AlertTriangle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useSysAdminSystemAgents,
  useCreateSystemAgent,
  useUpdateSystemAgent,
  useDeleteSystemAgent,
  useSysAdminSystemTools,
  useSysAdminSystemSkills,
  type SystemAgent,
} from '@/lib/use-sysadmin-system-catalog';

export const Route = createFileRoute('/sysadmin/studio/agents')({
  component: SysadminSystemAgentsPage,
});

interface FormState {
  slug: string;
  displayName: string;
  description: string;
  modelBrand: string;
  systemPrompt: string;
  assignedTools: string[];
  assignedSkills: string[];
  kind: '' | 'tool_helper' | 'skill_helper' | 'prompt_helper';
  category: string;
  isActive: boolean;
}

const EMPTY: FormState = {
  slug: '', displayName: '', description: '', modelBrand: 'argus-industrial',
  systemPrompt: '', assignedTools: [], assignedSkills: [], kind: '',
  category: 'general', isActive: true,
};

function rowToForm(r: SystemAgent): FormState {
  return {
    slug: r.slug,
    displayName: r.displayName,
    description: r.description,
    modelBrand: r.modelBrand,
    systemPrompt: r.systemPrompt,
    assignedTools: r.assignedTools,
    assignedSkills: r.assignedSkills,
    kind: r.kind ?? '',
    category: r.category,
    isActive: r.isActive,
  };
}

function SysadminSystemAgentsPage() {
  const { data, isLoading, error } = useSysAdminSystemAgents();
  const { data: toolsData } = useSysAdminSystemTools();
  const { data: skillsData } = useSysAdminSystemSkills();
  const create = useCreateSystemAgent();
  const [editing, setEditing] = useState<SystemAgent | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<SystemAgent | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);

  useEffect(() => {
    if (creating) setForm(EMPTY);
    else if (editing) setForm(rowToForm(editing));
  }, [creating, editing]);

  const update = useUpdateSystemAgent(editing?.id ?? '');
  const del = useDeleteSystemAgent();

  const items = useMemo(() => data?.items ?? [], [data]);
  const tools = useMemo(() => toolsData?.items ?? [], [toolsData]);
  const skills = useMemo(() => skillsData?.items ?? [], [skillsData]);

  const sheetOpen = creating || !!editing;
  const closeSheet = () => { setCreating(false); setEditing(null); };

  const toggleTool = (id: string) =>
    setForm({
      ...form,
      assignedTools: form.assignedTools.includes(id)
        ? form.assignedTools.filter((x) => x !== id)
        : [...form.assignedTools, id],
    });
  const toggleSkill = (id: string) =>
    setForm({
      ...form,
      assignedSkills: form.assignedSkills.includes(id)
        ? form.assignedSkills.filter((x) => x !== id)
        : [...form.assignedSkills, id],
    });

  const handleSave = () => {
    if (!form.slug.trim() || !/^[a-z0-9_.-]+$/.test(form.slug.trim())) return toast.error('Invalid slug');
    if (!form.displayName.trim() || !form.description.trim() || !form.systemPrompt.trim() || !form.modelBrand.trim()) {
      return toast.error('Display name, description, model brand, and system prompt are required.');
    }
    const body: Partial<SystemAgent> = {
      slug: form.slug.trim(),
      displayName: form.displayName.trim(),
      description: form.description.trim(),
      modelBrand: form.modelBrand.trim(),
      systemPrompt: form.systemPrompt,
      assignedTools: form.assignedTools,
      assignedSkills: form.assignedSkills,
      kind: form.kind === '' ? null : form.kind,
      category: form.category.trim() || 'general',
      isActive: form.isActive,
    };

    if (creating) {
      create.mutate(body, {
        onSuccess: () => { toast.success(`Agent "${body.slug}" created`); closeSheet(); },
        onError: (err) => toast.error('Create failed', { description: err.message }),
      });
    } else if (editing) {
      const { slug, ...rest } = body;
      void slug;
      update.mutate(rest, {
        onSuccess: () => { toast.success(`Agent "${editing.slug}" updated`); closeSheet(); },
        onError: (err) => toast.error('Update failed', { description: err.message }),
      });
    }
  };

  const handleDelete = () => {
    if (!confirmDelete) return;
    del.mutate({ id: confirmDelete.id }, {
      onSuccess: () => { toast.success(`Agent "${confirmDelete.slug}" deactivated`); setConfirmDelete(null); },
      onError: (err) => toast.error('Delete failed', { description: err.message }),
    });
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-base font-semibold flex items-center gap-2">
            <Bot className="h-4 w-4 text-muted-foreground" />
            System Agents
          </h2>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
            Sysadmin-authored agents incl. the three Studio Helper agents (kind = tool_helper / skill_helper / prompt_helper). Helpers run inside the tenant create-flow side panels (Phase 4).
          </p>
        </div>
        <Button size="sm" className="gap-1.5" onClick={() => setCreating(true)}>
          <Plus className="h-3.5 w-3.5" /> New agent
        </Button>
      </div>

      {isLoading ? (
        <Card><CardContent className="py-10 text-center text-muted-foreground animate-pulse">Loading…</CardContent></Card>
      ) : error ? (
        <Card className="border-destructive/30">
          <CardContent className="py-6 text-sm text-destructive flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>Failed to load: {error.message}</div>
          </CardContent>
        </Card>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">No system agents yet.</CardContent></Card>
      ) : (
        <Card>
          <CardContent className="px-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Slug</TableHead>
                  <TableHead>Display name</TableHead>
                  <TableHead className="w-32">Kind</TableHead>
                  <TableHead className="w-32">Model</TableHead>
                  <TableHead className="w-20">Tools</TableHead>
                  <TableHead className="w-20">Skills</TableHead>
                  <TableHead className="w-20">Active</TableHead>
                  <TableHead className="w-16">Ver</TableHead>
                  <TableHead className="w-20 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((a) => (
                  <TableRow key={a.id} className="cursor-pointer hover:bg-accent/30" onClick={() => setEditing(a)}>
                    <TableCell className="font-mono text-xs">{a.slug}</TableCell>
                    <TableCell className="text-sm">{a.displayName}</TableCell>
                    <TableCell>
                      {a.kind ? (
                        <Badge variant="outline" className="text-[10px] gap-1 bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/30">
                          <Sparkles className="h-2.5 w-2.5" />
                          {a.kind.replace('_', ' ')}
                        </Badge>
                      ) : (
                        <span className="text-[11px] text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{a.modelBrand}</TableCell>
                    <TableCell className="text-xs tabular-nums">{a.assignedTools.length}</TableCell>
                    <TableCell className="text-xs tabular-nums">{a.assignedSkills.length}</TableCell>
                    <TableCell><Badge variant={a.isActive ? 'secondary' : 'outline'} className="text-[10px]">{a.isActive ? 'Active' : 'Inactive'}</Badge></TableCell>
                    <TableCell className="text-xs tabular-nums">v{a.version}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10" onClick={() => setConfirmDelete(a)} title="Deactivate">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Sheet open={sheetOpen} onOpenChange={(o) => !o && closeSheet()}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{creating ? 'New system agent' : `Edit "${editing?.slug}"`}</SheetTitle>
            <SheetDescription>Helper agents (kind set) run in tenant create-flow side panels. Per Q5 they see structural metadata only — no tool calls, no document reads.</SheetDescription>
          </SheetHeader>
          <div className="space-y-3 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Slug</Label>
                <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} disabled={!creating} className="font-mono text-xs h-8" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Kind</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v as FormState['kind'] })}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="(none — ordinary agent)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">(none — ordinary agent)</SelectItem>
                    <SelectItem value="tool_helper">tool_helper</SelectItem>
                    <SelectItem value="skill_helper">skill_helper</SelectItem>
                    <SelectItem value="prompt_helper">prompt_helper</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Display name</Label>
              <Input value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} className="text-sm h-8" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Model brand</Label>
                <Input value={form.modelBrand} onChange={(e) => setForm({ ...form, modelBrand: e.target.value })} className="font-mono text-xs h-8" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Category</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="text-sm h-8" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">System prompt</Label>
              <Textarea value={form.systemPrompt} onChange={(e) => setForm({ ...form, systemPrompt: e.target.value })} rows={10} className="text-xs font-mono" spellCheck={false} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Assigned tools ({form.assignedTools.length})</Label>
              <div className="border rounded p-2 max-h-40 overflow-y-auto space-y-1">
                {tools.length === 0 ? (
                  <p className="text-[11px] text-muted-foreground italic">No system tools authored yet.</p>
                ) : tools.map((t) => (
                  <label key={t.id} className="flex items-center gap-2 text-xs hover:bg-accent/30 px-1 cursor-pointer">
                    <input type="checkbox" checked={form.assignedTools.includes(t.id)} onChange={() => toggleTool(t.id)} className="h-3 w-3" />
                    <span className="font-mono text-[10px] text-muted-foreground">{t.slug}</span>
                    <span>{t.displayName}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Assigned skills ({form.assignedSkills.length})</Label>
              <div className="border rounded p-2 max-h-40 overflow-y-auto space-y-1">
                {skills.length === 0 ? (
                  <p className="text-[11px] text-muted-foreground italic">No system skills authored yet.</p>
                ) : skills.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-xs hover:bg-accent/30 px-1 cursor-pointer">
                    <input type="checkbox" checked={form.assignedSkills.includes(s.id)} onChange={() => toggleSkill(s.id)} className="h-3 w-3" />
                    <span className="font-mono text-[10px] text-muted-foreground">{s.slug}</span>
                    <span>{s.displayName}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 h-8">
              <Switch checked={form.isActive} onCheckedChange={(v) => setForm({ ...form, isActive: v })} />
              <span className="text-xs">Active</span>
            </div>
            <div className="flex justify-end gap-2 pt-3">
              <Button variant="outline" onClick={closeSheet}>Cancel</Button>
              <Button onClick={handleSave} disabled={create.isPending || update.isPending}>
                {creating ? (create.isPending ? 'Creating…' : 'Create agent') : (update.isPending ? 'Saving…' : 'Save changes')}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={confirmDelete !== null} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate system agent</AlertDialogTitle>
            <AlertDialogDescription>
              Soft-deletes <code className="bg-muted px-1 rounded">{confirmDelete?.slug}</code>. Tenants stop seeing it. If this is a Helper agent (kind set), the corresponding Studio create-flow side panel will fall back to "(no helper available)" until it's re-activated.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={handleDelete}>Deactivate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
